import { ColumnDef, ColumnDefByColName } from '../../CORE/src/types'
import { AgreementTemplateDTO } from '../AgreementTemplateSelect/AgreementTemplateSelectService'
import { ModelPsAgreementRequests } from '../FlowPages/flowTypes'

/**
 * Form 内容
 * 飼い主様 氏名:
 * メールアドレス:
 * ペットお名前:
 * ペット種別:
 */

const cloneColNamesAndOverrides: {
  [key: string]: Partial<ColumnDef>
} = {
  send_method: {
    width: { xs: 48 },
    customLabel: (val) => {
      // URL発行 / 院内署名
      if (val === 'URL発行') {
        return 'URL発行 / 院内署名'
      }
      return val
    }
  },
  owner_name: {
    afterComponents: [
      {
        template: `<span class="btn btn-outline-primary btn-sm mt-1" @click.prevent="() => openSelectModal()">過去に送った人から選ぶ</span>`,
        computed: {
          modelFormInstance() {
            return $core.$utils.findNearestParentVueComponentByName(this, 'ModelForm')
          },
          modelFormColNames() {
            return Object.keys(this.modelFormInstance.columns)
          },
        },
        methods: {
          openSelectModal() {
            // @ts-ignore
            const filter = $core.$embAuth.user?.ps_clinic?.id
              ? {
                  clinic_id: {
                    // @ts-ignore
                    _eq: $core.$embAuth.user?.ps_clinic?.id,
                  },
                }
              : {}
            let modalId = ''
            modalId = $core.$modals.openListViewModal({
              modelName: 'ps_agreement_requests',
              filter,
              otherComponentProps: {
                itemClickEvent: (id, event, record) => {
                  this.modelFormColNames.reduce((acc, colName) => {
                    if (record[colName]) {
                      this.modelFormInstance.data[colName] = record[colName]
                    }
                    return acc
                  }, {} as Record<string, any>)
                  $core.$modals.closeModal(modalId)
                },
              },
            })
          },
        },
      },
    ],
    width: { xs: 48 },
  },
  owner_email: {
    width: { xs: 48 },
  },
  pet_name: {
    width: { xs: 48 },
  },
  pet_type: {
    inputComponent: 'RadioSelectInput',
    validate: { notEmpty: true },
    width: { xs: 48 },
  },
  pet_birthday: {
    width: { xs: 48, sm: 24 },
  },
  pet_age: {
    width: { xs: 48, sm: 24 },
  },
  pet_gender: {
    width: { xs: 48 },
  },
}

const cloneColNames = Object.keys(cloneColNamesAndOverrides)

let _quickCreateFormColumnsBase: ColumnDefByColName
const quickCreateFormColumnsBase = (): ColumnDefByColName => {
  if (!_quickCreateFormColumnsBase) {
    _quickCreateFormColumnsBase = cloneColNames.reduce(
      (acc: ColumnDefByColName, colName, index) => {
        const col: ColumnDef = { ...$core.$models.ps_agreement_requests.columns[colName] }
        col.groupKey = undefined
        col.inputAttrs = {
          autocorrect: 'false',
          autocomplete: 'off',
        }
        acc[colName] = Object.assign(col, cloneColNamesAndOverrides[colName])
        acc[colName].orderOnForm = index
        return acc
      },
      {},
    )
  }
  return {
    ..._quickCreateFormColumnsBase,
  }
}

type QuickCreateFormDataDefalut = {
  send_method: string
  owner_name: string
  owner_email: string
  pet_name: string
  pet_type: string
  [key: string]: any
}

const templateInputColNamePrefix = 'template_input'

export class AgreementTemplateQuickCreateNewAgreementFormService {
  // @ts-ignore
  agreementTemplate: AgreementTemplateDTO
  initPromise: Promise<void>
  constructor(public agreementTemplateId: string) {
    this.initPromise = this.init()
  }
  async init() {
    const agreementTemplate = await $core.$models.ps_agreement_template.findById(
      this.agreementTemplateId,
    )
    this.agreementTemplate = new AgreementTemplateDTO(agreementTemplate)
  }
  get quickCreateFormColumns(): ColumnDefByColName {
    const baseColumns = quickCreateFormColumnsBase()
    const columns: ColumnDefByColName = {}
    let orderOnForm = 100
    // ここで forceLetClinicUserInputOnSimpleSend の設定を参照して 入力してもらうカラムを生成する
    const sectionsSortedByOrder = Object.values(
      this.agreementTemplate.data.agreement_content.sectionById,
    ).sort((a, b) => a.section.order - b.section.order)
    sectionsSortedByOrder.map(section => {
      const sectionId = section.section.id
      if (section.excluded) {
        return // do nothing
      }
      Object.keys(section.selectableTemplateById).map(templateId => {
        const template = section.selectableTemplateById[templateId]
        if (!template.selected) {
          return // do nothing
        }
        if (template.forceLetClinicUserInputOnSimpleSend) {
          // ココで template の id を もった カラム名を生成する
          const user_define_items = template.template.user_define_items
          const defalutValues = template.userDefineItemsByKey
          if (user_define_items?.length > 0) {
            user_define_items.map((item, itemIndex) => {
              const colName = `${templateInputColNamePrefix}.${sectionId}.${templateId}.${item.key}`
              columns[colName] = {
                label: item.name,
                type: item.type as ColumnDefByColName[string]['type'],
                validate: {
                  ...(item.isRequired ? { notEmpty: true } : {}),
                },
                defaultValue: defalutValues?.[item.key]?.value || undefined,
                width:
                  ['STRING', 'NUMBER', 'DATEONLY'].indexOf(item.type) >= 0
                    ? { xs: 48, sm: 24 }
                    : { xs: 48 },
                // inputComponent: 'TextareaInput',
              }
              if (item.selectOptions?.trim()) {
                columns[colName].selections = () =>
                  item.selectOptions
                    .split('\n')
                    .map(l => l.trim())
                    .filter(l => l)
              }
              columns[colName].orderOnForm = orderOnForm
              orderOnForm++
              if (itemIndex === 0) {
                columns[colName].beforeComponents = [
                  {
                    template: template.template.name
                      ? `<div class="bold _mb-1 mt-2">${template.template.name}</div>${
                          template.template.desc_for_clinc
                            ? `<div class="small" style="white-space: pre-wrap; color: var(--bs-gray-800);" v-html="templateDescForClinc"></div>`
                            : ''
                        }`
                      : '<div></div>', // line break
                    computed: {
                      templateDescForClinc() {
                        return template.template.desc_for_clinc
                      },
                    },
                  },
                ]
              }
            })
          }
        }
      })
    })
    return {
      ...baseColumns,
      ...columns,
    }
  }

  parseDataWithTemplateAgreementContent(data: QuickCreateFormDataDefalut) {
    const agreement_content = JSON.parse(
      JSON.stringify(this.agreementTemplate.data.agreement_content),
    )
    // ここで テンプレートの ユーザー定義項目を パースして フォームに 追加する
    Object.keys(data).map(key => {
      if (key.startsWith(templateInputColNamePrefix)) {
        const [_prefix, sectionId, templateId, itemKey] = key.split('.')
        if (
          agreement_content.sectionById[sectionId]?.selectableTemplateById?.[templateId]
        ) {
          if (
            !agreement_content.sectionById[sectionId].selectableTemplateById[templateId]
              .userDefineItemsByKey
          ) {
            agreement_content.sectionById[sectionId].selectableTemplateById[
              templateId
            ].userDefineItemsByKey = {}
          }
          agreement_content.sectionById[sectionId].selectableTemplateById[
            templateId
          ].userDefineItemsByKey[itemKey] = {
            ...agreement_content.sectionById[sectionId].selectableTemplateById[templateId]
              .userDefineItemsByKey[itemKey],
            value: data[key],
          }
        }
      }
    })
    return agreement_content
  }

  async createNewAgreement(data: QuickCreateFormDataDefalut) {
    const userInfoText = `${data.owner_name}さん${data.send_method === 'メール' ? ` (メールアドレス ${data.owner_email})` : ''}`
    const message = data.send_method === 'メール' ? `${userInfoText} へ 同意依頼を送信します。よろしいですか？` : `${userInfoText} へ 同意依頼を作成し、URLを発行します。よろしいですか？`
    if ((await $core.$toast.confirmDialog(message)) !== true) {
      return false
    }
    $core.$loading.start('同意依頼を送信中...', 'overlay')
    try {
      const agreement_content = this.parseDataWithTemplateAgreementContent(data)
      // `ps_agreement_requests` を 新規作成
      const agreementRequestData: Partial<ModelPsAgreementRequests> = {
        ...data,
        agreement_type_id: this.agreementTemplate.data.agreement_type_id,
        agreement_content,
        agreement_template: this.agreementTemplateId,
        admin_title: this.agreementTemplate.data.title,
        // @ts-ignore ログインユーザーの医院を設定
        clinic_id: $core.$embAuth.user.ps_clinic?.id || $core.$embAuth.user.ps_clinic,
        sent_at: $core.$dayjs().format('YYYY-MM-DD HH:mm:ss'),
        // この param で 送信となる
        lastSentRequestedAt: $core.$dayjs().format('YYYY-MM-DD HH:mm:ss'),
        status: '送信済み',
      }
      const agreementRequestResponse = await $core.$storeMethods.upsert({
        modelName: 'ps_agreement_requests',
        data: agreementRequestData,
        quiet: true,
      })
      $core.$toast.success(`${userInfoText} へ 同意依頼を送信しました。`)
      const createdAgreementRequestId = agreementRequestResponse.data?.[0]?.id
      return {
        createdAgreementRequestId,
        agreementRequestData,
      }
    } catch (error) {
      console.error(error)
      return false
    } finally {
      $core.$loading.stop()
    }
  }

  /**
   * 簡易送信フォームから同意依頼を「下書き」として保存する（メール送信・送信日時の更新は行わない）。
   * 確認ダイアログ → upsert → 成功トーストまでを一連で実行する公開エントリポイント。
   */
  async saveDraft(
    data: QuickCreateFormDataDefalut,
  ): Promise<{ createdAgreementRequestId: number } | false> {
    if (!(await this._confirmSaveDraft())) {
      return false
    }
    $core.$loading.start('保存中...', 'overlay')
    try {
      const agreementRequestData = this._buildDraftAgreementRequestPayload(data)
      const response = await $core.$storeMethods.upsert({
        modelName: 'ps_agreement_requests',
        data: agreementRequestData,
        quiet: true,
      })
      const createdAgreementRequestId = response.data?.[0]?.id
      if (createdAgreementRequestId === undefined || createdAgreementRequestId === null) {
        console.error('[AgreementTemplateQuickCreateNewAgreementFormService.saveDraft] id なし', response)
        $core.$toast.error('保存に失敗しました')
        return false
      }
      $core.$toast.success('下書きとして保存しました')
      return { createdAgreementRequestId }
    } catch (error) {
      console.error(error)
      $core.$toast.error('保存に失敗しました')
      return false
    } finally {
      $core.$loading.stop()
    }
  }

  /** 下書き保存の確認ダイアログ（キャンセル時は false） */
  private async _confirmSaveDraft(): Promise<boolean> {
    return (
      (await $core.$toast.confirmDialog(
        '下書きとして保存します（送信はされません）。よろしいですか？',
      )) === true
    )
  }

  /**
   * フォームデータから DB カラムのみを抽出する。
   * template_input.* は agreement_content 側にマージされるため upsert 対象から除外する。
   */
  private _filterDbColumnsFromFormData(data: QuickCreateFormDataDefalut): Record<string, any> {
    const modelColumns = $core.$models.ps_agreement_requests.columns
    const dbData: Record<string, any> = {}
    for (const key of Object.keys(data)) {
      if (key.startsWith(templateInputColNamePrefix)) {
        continue
      }
      if (!(key in modelColumns)) {
        continue
      }
      dbData[key] = data[key]
    }
    return dbData
  }

  /**
   * 下書き upsert 用のレコードペイロードを組み立てる。
   * lastSentRequestedAt / sent_at はセットしない（サーバー側の送信処理を起動させない）。
   */
  private _buildDraftAgreementRequestPayload(
    data: QuickCreateFormDataDefalut,
  ): Partial<ModelPsAgreementRequests> {
    const agreement_content = this.parseDataWithTemplateAgreementContent(data)
    const dbData = this._filterDbColumnsFromFormData(data)
    return {
      ...dbData,
      agreement_type_id: this.agreementTemplate.data.agreement_type_id,
      agreement_content,
      agreement_template: this.agreementTemplateId,
      admin_title: this.agreementTemplate.data.title,
      // @ts-ignore ログインユーザーの医院を設定
      clinic_id: $core.$embAuth.user.ps_clinic?.id || $core.$embAuth.user.ps_clinic,
      status: '下書き',
    }
  }
}
